/**
 * SSE-aware polling for the unseen helper-feed count. Real-time updates via SSE
 * when connected (a `helper_match.created` event invalidates `helperFeedCountKey()`,
 * triggering an immediate refetch); falls back to 10s polling when SSE is down.
 *
 * Two hooks (mirrors `useNotificationCount` / `useNotificationCountValue`):
 *  - `useHelperFeedCount` — owns the polling lifecycle. Mount once in BadgeProvider.
 *  - `useHelperFeedCountValue` — read-only subscriber for components that just
 *    need the current value. Avoids spinning up a duplicate polling timer
 *    (each QueryObserver maintains its own `refetchInterval`).
 */

import { useQuery, skipToken } from '@tanstack/react-query';
import { getHelpableCount } from '@api-client/api-client';
import { helperFeedCountKey } from '@lib/cache/helperFeed';
import type { HelpableUnseenCount } from '@models/api';
import { useAuth } from './useAuth';
import { useEventStreamState } from './useEventStreamState';
import { AuthSessionState } from '@providers/auth/authTypes';

/**
 * Hook for polling the unseen helper-feed count. Mount once globally in BadgeProvider.
 *
 * @param enabled - Whether to enable polling (default: true)
 * @param pollingInterval - Polling interval in milliseconds (default: 10000 = 10 seconds)
 * @returns React Query result with unseen count data (`{ unseen_count }`)
 */
export function useHelperFeedCount(enabled = true, pollingInterval = 10000) {
  const { user, sessionState } = useAuth();
  const { isConnected } = useEventStreamState();
  // Issue #567: Stop polling during recovery so the invalid_grant cascade
  // doesn't get amplified by 10-second tokenless requests.
  const isSessionBlocked =
    sessionState === AuthSessionState.SESSION_EXPIRED ||
    sessionState === AuthSessionState.REAUTHENTICATING;
  const shouldPoll = enabled && !isSessionBlocked;

  return useQuery<HelpableUnseenCount>({
    queryKey: helperFeedCountKey(),
    queryFn: () => getHelpableCount().then(r => r.data),
    enabled: !!user && shouldPoll,

    // AIDEV-NOTE: Two orthogonal gates — SSE absorbs real-time updates when connected
    // (helper_match.created invalidates this key), and Issue #567 pauses polling during
    // auth recovery. Both must be satisfied to poll.
    refetchInterval: isConnected ? false : shouldPoll ? pollingInterval : false,
    refetchIntervalInBackground: false, // Foreground reconnect + full-sync handles catch-up
    refetchOnMount: 'always', // CRITICAL: Fetch immediately on mount for instant badge
    refetchOnWindowFocus: true, // Refetch when user returns to tab

    staleTime: pollingInterval * 0.8, // Consider data fresh for 80% of interval
    gcTime: pollingInterval * 2, // Keep in cache for 2x interval
  });
}

/**
 * Read-only subscriber for the unseen helper-feed count cache.
 *
 * Returns `data` reactively from the same cache key as `useHelperFeedCount`.
 * Does NOT spin up its own polling timer — relies on `useHelperFeedCount`
 * (mounted in BadgeProvider) to drive fetches. Use this in components that
 * consume the count but don't own the polling lifecycle (the combined bell badge).
 */
export function useHelperFeedCountValue() {
  return useQuery<HelpableUnseenCount>({
    queryKey: helperFeedCountKey(),
    queryFn: skipToken,
  });
}
